import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { Brain, Plus, Trash2, X } from 'lucide-react';
import { aiAgentsAPI } from '../../services/api/aiAgents';
import toast from '../../services/toast';

export default function MemoryPanel({ open, onClose }: any) {
  const [memories, setMemories] = useState<any[]>([]);
  const [newMemory, setNewMemory] = useState('');
  const [loading, setLoading] = useState(false);
  const reduceMotion = useReducedMotion();

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const data = await aiAgentsAPI.listMemory();
      const items = data.items ?? data.memories ?? data ?? [];
      setMemories(
        (Array.isArray(items) ? items : []).map((m) => ({
          id: m.id,
          content: m.value ?? m.content ?? '',
          key: m.key,
        }))
      );
    } catch {
      setMemories([]);
      toast.error('Failed to load memories');
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    load();
  }, [load]);

  const addMemory = async () => {
    const text = newMemory.trim();
    if (!text) return;
    const key = `${text.slice(0, 32).replace(/\s+/g, '_').toLowerCase()}_${Date.now().toString(36)}`;
    try {
      const created = await aiAgentsAPI.createMemory({ key, value: text });
      const item = created.item ?? created;
      setMemories((prev) => [
        { id: item.id, content: item.value ?? text, key: item.key ?? key },
        ...prev,
      ]);
      setNewMemory('');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save memory');
    }
  };

  const deleteMemory = async (id) => {
    const removed = memories.find((m) => m.id === id);
    setMemories((prev) => prev.filter((m) => m.id !== id));
    try {
      await aiAgentsAPI.deleteMemory(id);
    } catch (err) {
      if (removed) {
        setMemories((prev) => [removed, ...prev]);
      }
      toast.error((err as Error).message || 'Failed to delete memory');
    }
  };

  const panelMotion: any = reduceMotion
    ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { y: 12, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: 12, opacity: 0 },
        transition: { duration: 0.2, ease: 'easeOut' as const },
      };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...panelMotion}
          className="absolute bottom-full right-0 z-30 mb-2 w-80 rounded-2xl border border-dark-border bg-dark-surface shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-dark-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-vivid-green-cyan" aria-hidden />
              <span className="text-sm font-semibold text-slate-100">Vanti Remembers</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-icon-mdl h-7 w-7"
              aria-label="Close memory panel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <motion.div layout={false} className="max-h-56 space-y-1.5 overflow-y-auto p-3">
            {loading && <p className="py-2 text-center text-xs text-slate-400">Loading…</p>}
            {!loading && memories.length === 0 && (
              <p className="py-2 text-center text-xs text-slate-400">Nothing remembered yet</p>
            )}
            {memories.map((m) => (
              <div
                key={m.id}
                className="group flex items-start gap-2 rounded-lg bg-dark-base px-2.5 py-2"
              >
                <p className="flex-1 text-xs text-slate-300">{m.content}</p>
                <button
                  type="button"
                  onClick={() => deleteMemory(m.id)}
                  className="hidden h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 hover:text-vivid-red group-hover:flex"
                  aria-label="Forget this"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </motion.div>
          <motion.div layout={false} className="border-t border-dark-border p-3">
            <div className="flex gap-2">
              <input
                value={newMemory}
                onChange={(e) => setNewMemory(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addMemory()}
                placeholder="Add a memory…"
                className="input-mdl flex-1 py-1.5 text-xs"
              />
              <button
                type="button"
                onClick={addMemory}
                className="btn-primary-mdl px-2.5 py-1.5 text-xs"
                aria-label="Save memory"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
